import { Table } from '@tanstack/react-table';
import {
  ColumnGroup,
  ColumnSelectorConfig,
  FilterConfig,
  RowHeightConfig,
  RowHeightView,
  SearchConfigProps,
  TableProps,
} from '../types';
import { GlobalFilterTool } from './GlobalFilterTool';
import { GlobalGroupingTool } from './GlobalGroupingTool';
import { GlobalSortTool } from './GlobalSortTool';
import { TableCustomizeColumns } from './TableCustomizeColumns';
import { TableSearch } from './TableSearch';
import { TableViewMenu } from './TableViewMenu';

export function TableToolbar<TData, TValue>({
  addRowConfig,
  columnSelectorConfig,
  searchConfig,
  tableViewConfig,
  table,
  rowHeightView,
  setRowHeightView,
  handleSearch,
  customActions,
  groupingConfig,
  columnGroups,
  onApplyColumnGroups,
  filterConfig,
}: {
  addRowConfig: TableProps<TData, TValue>['addRowConfig'];
  columnSelectorConfig: ColumnSelectorConfig;
  searchConfig: SearchConfigProps;
  tableViewConfig: RowHeightConfig;
  table: Table<TData>;
  rowHeightView: RowHeightView;
  setRowHeightView: (rowHeight: RowHeightView) => void;
  handleSearch: (searchValue: string, isFromEffect?: boolean) => void;
  customActions?: () => React.ReactNode;
  groupingConfig?: TableProps<TData, TValue>['groupingConfig'];
  columnGroups: ColumnGroup<TData, TValue>[];
  onApplyColumnGroups: (groups: ColumnGroup<TData, TValue>[]) => void;
  filterConfig?: FilterConfig;
}) {
  return (
    <div className="flex items-center justify-between pb-2xl">
      <div className="flex items-center gap-4 flex-1">
        {addRowConfig?.isEnabled ? addRowConfig.renderAddRow() : null}

        <GlobalFilterTool filterConfig={filterConfig} />

        <GlobalSortTool table={table} columnGroups={columnGroups} />

        <GlobalGroupingTool groupingConfig={groupingConfig} />

        {columnSelectorConfig.isEnabled ? (
          <TableCustomizeColumns
            table={table}
            groups={columnGroups}
            onApply={onApplyColumnGroups}
            onReset={() => {
              // no-op here; DataTable handles default state via props
            }}
            placeholder={columnSelectorConfig.placeholder}
            defaultVisibleColumns={columnSelectorConfig.defaultVisibleColumns}
            onColumnVisibilityChange={
              columnSelectorConfig.onColumnVisibilityChange
            }
          />
        ) : null}

        {tableViewConfig.isEnabled ? (
          <TableViewMenu
            rowHeightView={rowHeightView}
            setRowHeightView={setRowHeightView}
          />
        ) : null}

        {customActions && customActions()}
      </div>

      <div>
        {searchConfig.isEnabled && (
          <TableSearch
            searchConfig={searchConfig}
            handleSearch={handleSearch}
          />
        )}
      </div>
    </div>
  );
}
